import { Button, Input, Label, Text } from "@scott/ui/client";
import { IconAlertCircle, IconStore } from "@scott/ui/icons";
import { Alert, AlertDescription, AlertTitle } from "@ui-b2b/server";
import type React from "react";

export type LoginPageProps = {
  customerId: string;
  password: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onCustomerIdChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  forgotPasswordHref: string;
  dictionary: {
    title: string;
    customerId: string;
    customerIdPlaceholder: string;
    password: string;
    passwordPlaceholder: string;
    forgotPassword: string;
    signIn: string;
  };
  isLoading?: boolean;
  errorTitle?: string;
  errorDescription?: string;
};

export function LoginPage(props: Readonly<LoginPageProps>) {
  return (
    <section className="flex min-h-screen w-full justify-center overflow-x-hidden bg-background">
      <div className="flex w-full items-center justify-center px-6 py-16">
        <div className="flex w-96 flex-col gap-6">
          {/* Brand Logo */}
          <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-foreground">
            <IconStore className="size-4 text-foreground" />
          </div>

          {/* Form Container */}
          <form
            className="flex w-full flex-col gap-6"
            onSubmit={props.onSubmit}
          >
            {/* Title */}
            <div className="flex w-full items-center justify-start pb-2">
              <Text variant="h2">{props.dictionary.title}</Text>
            </div>

            {/* Error Message */}
            {props.errorTitle ? (
              <Alert className="flex flex-col rounded-md px-4 py-3">
                <IconAlertCircle />
                <AlertTitle className="mb-0 font-semibold text-base">
                  {props.errorTitle}
                </AlertTitle>
                {props.errorDescription ? (
                  <AlertDescription>{props.errorDescription}</AlertDescription>
                ) : null}
              </Alert>
            ) : null}

            {/* Customer ID Input */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="customer-id">{props.dictionary.customerId}</Label>
              <Input
                disabled={props.isLoading}
                id="customer-id"
                name="id"
                onChange={props.onCustomerIdChange}
                placeholder={props.dictionary.customerIdPlaceholder}
                required
                type="text"
                value={props.customerId}
              />
            </div>

            {/* Password Input */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="password-input">
                {props.dictionary.password}
              </Label>
              <Input
                disabled={props.isLoading}
                id="password-input"
                name="password"
                onChange={props.onPasswordChange}
                placeholder={props.dictionary.passwordPlaceholder}
                required
                type="password"
                value={props.password}
              />
            </div>

            {/* Forgot Password Link */}
            <div className="flex w-full items-center">
              <Text variant="body-md">
                <a
                  className="text-foreground underline-offset-4 hover:underline"
                  href={props.forgotPasswordHref}
                >
                  {props.dictionary.forgotPassword}
                </a>
              </Text>
            </div>

            {/* Sign In Button */}
            <Button disabled={props.isLoading} type="submit" variant="default">
              {props.dictionary.signIn}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
